import { useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';

const SWATCHES = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#a855f7', // purple
  '#ec4899', // pink
];

/** Human-readable names for the curated palette. Surfaced via `aria-label`
 * (screen readers) and `title` (sighted hover) so the popover is usable
 * without sight — bare hex values like `#ef4444` aren't accessible. */
const COLOR_NAMES: Record<string, string> = {
  '#ef4444': 'Red',
  '#f97316': 'Orange',
  '#eab308': 'Yellow',
  '#22c55e': 'Green',
  '#06b6d4': 'Cyan',
  '#3b82f6': 'Blue',
  '#a855f7': 'Purple',
  '#ec4899': 'Pink',
};

export function ColorPopover({
  editor,
  mode,
  onClose,
  anchor,
}: {
  editor: Editor;
  mode: 'color' | 'highlight' | 'cell-bg';
  onClose: () => void;
  /** Trigger button's viewport rect — popover anchors to its bottom-left.
   * Position-fixed (not absolute) so the toolbar's `overflow-x-auto` doesn't
   * clip the popover. */
  anchor: DOMRect;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Click-outside + Escape-key dismiss. Click-outside also skips popover
  // trigger buttons (marked `data-popover-trigger`) so its React onClick can
  // manage open/close state without fighting this listener — important when
  // one popover is open and the user clicks a sibling trigger to switch
  // popover (otherwise both setState calls batch and the listener's `null`
  // clobbers the new mode). Escape gives keyboard-only users a way out.
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Element | null;
      if (!target) return;
      if (ref.current && ref.current.contains(target)) return;
      if (target.closest?.('[data-popover-trigger]')) return;
      onClose();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  function apply(color: string | null) {
    const chain = editor.chain().focus();
    if (mode === 'color') {
      if (color) (chain as any).setColor(color).run();
      else (chain as any).unsetColor().run();
    } else if (mode === 'highlight') {
      if (color) (chain as any).setHighlight({ color }).run();
      else (chain as any).unsetHighlight().run();
    } else {
      // cell-bg — uses the TableCellEx command. Same 8-swatch palette as
      // text colour / highlight; null clears. Guard the call: if TableCellEx
      // ever fails to load (extension order regression) the chain method is
      // undefined and we'd silently lose the user's pick. The `if` lets us
      // log diagnostically and at least keep the popover open.
      const setBg = (chain as any).setCellBackground;
      if (typeof setBg !== 'function') {
        console.warn(
          'vault: setCellBackground command missing — TableCellEx may not be registered',
        );
        return;
      }
      setBg.call(chain, color).run();
    }
    onClose();
  }

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: anchor.bottom + 4,
        left: anchor.left,
        zIndex: 50,
      }}
      className="rounded-md border border-ink-700 bg-ink-900 p-2 shadow-lg"
    >
      <div className="grid grid-cols-4 gap-1">
        {SWATCHES.map((c) => {
          const name = COLOR_NAMES[c] ?? c;
          return (
            <button
              type="button"
              key={c}
              onClick={() => apply(c)}
              className="w-6 h-6 rounded border border-ink-700 hover:ring-2 hover:ring-dragon-500 hover:ring-offset-1 hover:ring-offset-ink-900"
              style={{ backgroundColor: c }}
              title={`${name} (${c})`}
              aria-label={name}
            />
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => apply(null)}
        className="mt-2 w-full text-xs text-ink-300 hover:bg-ink-800 hover:text-white py-1 rounded border border-ink-700"
        aria-label="Clear colour"
      >
        Clear
      </button>
    </div>
  );
}
